import { cn } from "@/lib/utils";
import { LucideIcon, TrendingUp, TrendingDown, Loader2 } from "lucide-react";

interface StatCardProps {
  title: string;
  value: string | number;
  icon: LucideIcon;
  trend?: {
    value: string;
    isPositive: boolean;
    label: string;
  };
  loading?: boolean;
  href?: string;
  className?: string;
  gradient?: string;
}

export function StatCard({
  title,
  value,
  icon: Icon,
  trend,
  loading,
  href,
  className,
  gradient = "from-blue-400 to-blue-600",
}: StatCardProps) {
  const content = (
    <div
      className={cn(
        "relative overflow-hidden rounded-xl border border-gray-200 bg-white p-6 shadow-lg transition-all duration-200 dark:border-gray-700 dark:bg-gray-800",
        href && "cursor-pointer hover:scale-[1.02] hover:shadow-xl",
        className,
      )}
    >
      {/* Background gradient overlay */}
      <div className="absolute inset-0 opacity-5">
        <div className={cn("h-full w-full bg-gradient-to-br", gradient)} />
      </div>

      <div className="relative">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <p className="text-sm font-medium uppercase tracking-wide text-gray-600 dark:text-gray-300">
              {title}
            </p>
            <div className="mt-2">
              {loading ? (
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
              ) : (
                <p className="text-3xl font-bold text-gray-900 dark:text-white">
                  {typeof value === "number" ? value.toLocaleString() : value}
                </p>
              )}
            </div>
            {trend && !loading && (
              <div className="mt-2 flex items-center text-sm">
                {trend.isPositive ? (
                  <TrendingUp className="mr-1 h-4 w-4 text-green-500" />
                ) : (
                  <TrendingDown className="mr-1 h-4 w-4 text-red-500" />
                )}
                <span
                  className={cn(
                    "mr-1 font-medium",
                    trend.isPositive
                      ? "text-green-600 dark:text-green-400"
                      : "text-red-600 dark:text-red-400",
                  )}
                >
                  {trend.value}
                </span>
                <span className="text-gray-600 dark:text-gray-400">
                  {trend.label}
                </span>
              </div>
            )}
          </div>

          <div className={cn("rounded-lg bg-gradient-to-br p-3", gradient)}>
            <Icon className="h-6 w-6 text-white" />
          </div>
        </div>
      </div>
    </div>
  );

  if (href) {
    return (
      <a href={href} className="block">
        {content}
      </a>
    );
  }

  return content;
}

interface MiniStatCardProps {
  title: string;
  value: string | number;
  icon: LucideIcon;
  loading?: boolean;
  className?: string;
  color?: "blue" | "green" | "purple" | "red" | "orange";
}

export function MiniStatCard({
  title,
  value,
  icon: Icon,
  loading,
  className,
  color = "blue",
}: MiniStatCardProps) {
  const colorClasses = {
    blue: "from-blue-400 to-blue-600",
    green: "from-green-400 to-green-600",
    purple: "from-purple-400 to-purple-600",
    red: "from-red-400 to-red-600",
    orange: "from-orange-400 to-orange-600",
  };

  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-lg border border-gray-200 bg-white p-4 shadow-md dark:border-gray-700 dark:bg-gray-800",
        className,
      )}
    >
      <div className="flex items-center">
        <div
          className={cn(
            "rounded-md bg-gradient-to-br p-2",
            colorClasses[color],
          )}
        >
          <Icon className="h-4 w-4 text-white" />
        </div>
        <div className="ml-3">
          <p className="text-sm font-medium text-gray-600 dark:text-gray-300">
            {title}
          </p>
          {loading ? (
            <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
          ) : (
            <p className="text-lg font-semibold text-gray-900 dark:text-white">
              {typeof value === "number" ? value.toLocaleString() : value}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
